import { catToBucket } from '../types/cost.js';
import { ACTIVE_STATUSES } from '../types/car.js';
import { MONTH_LABELS } from './dates.js';
import { restwaarde } from './depreciation.js';
import { isEigenBezit } from './lease.js';
import { TCO_POSTEN, tcoMaandenVoorAuto } from './tco.js';
/**
 * De waarde zoals hij op een tegel hoort te staan.
 *
 * `euroPerEenheid` staat er apart in omdat een prijs per liter twee decimalen
 * nodig heeft en een bedrag van € 1.842 niet: € 1,84 afronden op hele euro's
 * maakt de tegel betekenisloos, en € 1.842,00 leest als een boekhouding.
 */
export function trackerWaarde(waarde, eenheid = 'euro') {
    const nl = (n, decimalen = 0) => n.toLocaleString('nl-NL', {
        minimumFractionDigits: decimalen,
        maximumFractionDigits: decimalen,
    });
    switch (eenheid) {
        case 'euroPerEenheid':
            return `€ ${nl(waarde, 2)}`;
        case 'liter':
            return `${nl(waarde, waarde < 100 ? 1 : 0)} L`;
        case 'kwh':
            return `${nl(waarde, waarde < 100 ? 1 : 0)} kWh`;
        case 'km':
            return `${nl(waarde)} km`;
        case 'aantal':
            return nl(waarde);
        default:
            return `€ ${nl(waarde, 2)}`;
    }
}
/** `a, b en c` — a Dutch list, for the tooltips. */
function opsomming(delen) {
    if (delen.length <= 1)
        return delen[0] ?? '';
    return `${delen.slice(0, -1).join(', ')} en ${delen[delen.length - 1]}`;
}
/** Last day of the month `YYYY-MM`. */
function dagenInMaand(maand) {
    const [j, m] = maand.split('-').map(Number);
    if (!j || !m)
        return 31;
    return new Date(Date.UTC(j, m, 0)).getUTCDate();
}
function maandIndex(maand) {
    const [j, m] = maand.split('-').map(Number);
    return (j ?? 0) * 12 + ((m ?? 1) - 1);
}
function indexNaarMaand(idx) {
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}
/**
 * A contiguous run of `aantal` months ending at `laatste`.
 *
 * Contiguous on purpose, rather than "the months that have cost lines": a month
 * without a single transaction is a real dip and has to keep its slot on the
 * axis, otherwise the sparkline closes the gap and the dip disappears.
 */
export function trackerMaanden(laatste, aantal) {
    const eind = maandIndex(laatste);
    const uit = [];
    for (let i = eind - aantal + 1; i <= eind; i++)
        uit.push(indexNaarMaand(i));
    return uit;
}
export function buildTrackers({ autos, maanden, dagGrens, now = new Date(), }) {
    if (!maanden.length)
        return [];
    const van = maanden[0];
    const tot = maanden[maanden.length - 1];
    const vast = new Map();
    const gemeten = new Map();
    const actief = new Map();
    for (const m of maanden) {
        vast.set(m, 0);
        gemeten.set(m, 0);
        actief.set(m, 0);
    }
    const actieveIds = new Set();
    for (const a of autos) {
        const perMaand = tcoMaandenVoorAuto({
            car: a.car,
            regels: a.regels,
            tarieven: a.tarieven,
            van,
            tot,
            now,
        });
        for (const m of maanden) {
            const mm = perMaand.get(m);
            if (!mm)
                continue;
            if (mm.actief) {
                actief.set(m, actief.get(m) + 1);
                actieveIds.add(a.car.id);
            }
            // Alle vaste posten, dus ook verzekering: die telt gewoon mee en staat op
            // nul zolang niemand een premie heeft vastgelegd.
            for (const p of TCO_POSTEN)
                if (p.vast)
                    vast.set(m, vast.get(m) + mm.posten[p.key]);
        }
        // Gemeten kosten met de dagafkap, zodat elke maand over dezelfde spanne telt.
        // Vaste lasten gaan hier bewust niet doorheen: die zijn maandbedragen.
        for (const r of a.regels) {
            if (!gemeten.has(r.maand))
                continue;
            if (dagGrens != null && (r.datum == null || Number(r.datum.slice(8, 10)) > dagGrens))
                continue;
            gemeten.set(r.maand, gemeten.get(r.maand) + (Number(r.bedragExcl) || 0));
        }
    }
    // Boekwaarde per maandeinde, over het bezit dat we nú hebben. Een verkochte
    // auto telt niet meer mee, ook niet in de maanden dat hij er nog stond: de
    // vraag achter deze regel is wat het huidige wagenpark waard is.
    const eigen = autos.filter((a) => isEigenBezit(a.car) && ACTIVE_STATUSES.includes(a.car.status));
    const restPerMaand = new Map();
    for (const m of maanden) {
        const eindeMaand = new Date(`${m}-${String(dagenInMaand(m)).padStart(2, '0')}T00:00:00`);
        const peil = eindeMaand > now ? now : eindeMaand;
        restPerMaand.set(m, eigen.reduce((s, a) => s + (restwaarde(a.car, peil) ?? 0), 0));
    }
    const punt = (m, waarde, n) => ({
        maand: m,
        label: MONTH_LABELS[Number(m.slice(5, 7)) - 1] ?? '',
        waarde,
        autos: n,
    });
    const spanne = dagGrens == null
        ? 'de hele maand, vergeleken met de hele maand ervoor'
        : `dag 1 t/m ${dagGrens}, vergeleken met dezelfde dagen van de maand ervoor`;
    // Welke vaste post ergens is vastgelegd en welke nergens. Dezelfde vraag als
    // het TCO-paneel stelt, dus ook hetzelfde antwoord: nul in het totaal is geen
    // bewijs van een gat — een leasetermijn is nul op een vloot die we zelf bezitten.
    // Uit `TCO_POSTEN` en niet uit de data, zodat de uitleg meegroeit als er een
    // soort vaste last bijkomt. `vastOverig` blijft eruit: dat is het vangnet voor
    // een soort die nog niet gemapt is, geen post op zichzelf.
    const vastePosten = opsomming(TCO_POSTEN.filter((p) => p.vast && p.key !== 'vastOverig').map((p, i) => i === 0 ? p.label : p.label.toLowerCase()));
    return [
        {
            key: 'tco',
            label: 'TCO per maand',
            reeks: maanden.map((m) => punt(m, vast.get(m) + gemeten.get(m), actief.get(m))),
            sub: `De vaste lasten van de hele maand plus de gemeten kosten over ${spanne}.`,
            kleur: 'kosten',
            perAuto: true,
        },
        {
            key: 'vast',
            label: 'Vaste lasten',
            reeks: maanden.map((m) => punt(m, vast.get(m), actief.get(m))),
            sub: `${vastePosten}, over de hele maand.`,
            kleur: 'kosten',
            perAuto: true,
        },
        {
            key: 'variabel',
            label: 'Variabele kosten',
            reeks: maanden.map((m) => punt(m, gemeten.get(m), actief.get(m))),
            sub: `Brandstof, laden, parkeren, wassen en onderhoud over ${spanne}.`,
            kleur: 'kosten',
            perAuto: true,
        },
        {
            key: 'restwaarde',
            label: 'Restwaarde wagenpark',
            reeks: maanden.map((m) => punt(m, restPerMaand.get(m), eigen.length)),
            // Niet "→ 20%": vier auto's schrijven af naar een vaste € 4.500, en dat is
            // precies waarom `Car.eindwaarde` bestaat.
            sub: `De boekwaarde van ${eigen.length} auto's in eigen bezit, aan het eind van de maand.`,
            kleur: 'neutraal',
            perAuto: true,
        },
    ];
}
/**
 * What a litre, a kWh, a parking action and a wash cost, month by month.
 *
 * Standing on their own these say almost nothing — € 1,89 per litre is neither
 * good nor bad. Against last month they do: fuel over 2026 averages € 1,89 while
 * the whole run averages € 1,80, so the price moved and only a comparison shows
 * it. Same treatment as the fleet trackers, same equal-span rule, so a running
 * month is not measured against a full one.
 *
 * Two of the four have a real denominator (litres, kWh) and two are counted per
 * transaction, because a parking action and a wash have no quantity on the
 * invoice. That is why they are prices per *action*, not per unit.
 */
export function buildPrijsTrackers({ regels, maanden, dagGrens, }) {
    if (!maanden.length)
        return [];
    const soorten = ['brandstof', 'laden', 'parkeren', 'wassen'];
    const per = new Map(soorten.map((s) => [s, new Map(maanden.map((m) => [m, { bedrag: 0, hoeveelheid: 0 }]))]));
    for (const r of regels) {
        const pot = per.get(catToBucket(r.categorie))?.get(r.maand);
        if (!pot)
            continue;
        if (dagGrens != null && (r.datum == null || Number(r.datum.slice(8, 10)) > dagGrens))
            continue;
        const bucket = catToBucket(r.categorie);
        // Brandstof en laden delen door de geleverde hoeveelheid; een regel zonder
        // liters of kWh (een pasfee, een correctie) hoort dus in geen van beide.
        if (bucket === 'brandstof' || bucket === 'laden') {
            const eenheid = bucket === 'brandstof' ? 'LTR' : 'KWH';
            if (r.eenheid !== eenheid || !r.hoeveelheid)
                continue;
            pot.hoeveelheid += r.hoeveelheid;
        }
        else {
            pot.hoeveelheid += 1;
        }
        pot.bedrag += Number(r.bedragExcl) || 0;
    }
    const opzet = [
        {
            soort: 'brandstof',
            key: 'per-liter',
            label: 'Per liter',
            sub: 'Alle brandstof gedeeld door de getankte liters.',
        },
        {
            soort: 'laden',
            key: 'per-kwh',
            label: 'Per kWh',
            sub: 'Alle laadtransacties gedeeld door de geladen kWh.',
        },
        {
            soort: 'parkeren',
            key: 'per-parkeeractie',
            label: 'Per parkeeractie',
            sub: 'Gemiddeld bedrag per parkeertransactie; er staat geen aantal uren op de factuur.',
        },
        {
            soort: 'wassen',
            key: 'per-wasbeurt',
            label: 'Per wasbeurt',
            sub: 'Gemiddeld bedrag per wastransactie.',
        },
    ];
    const spanne = dagGrens == null
        ? 'Hele maanden, vergeleken met de maand ervoor.'
        : `Dag 1 t/m ${dagGrens}, vergeleken met dezelfde dagen van de maand ervoor.`;
    return opzet
        .map(({ soort, key, label, sub }) => {
        const potten = per.get(soort);
        const reeks = maanden.map((m) => {
            const p = potten.get(m);
            const leeg = p.hoeveelheid <= 0;
            return {
                maand: m,
                label: MONTH_LABELS[Number(m.slice(5, 7)) - 1] ?? '',
                waarde: leeg ? 0 : p.bedrag / p.hoeveelheid,
                autos: 0,
                ...(leeg ? { leeg: true } : {}),
            };
        });
        return {
            key,
            label,
            reeks,
            sub: `${sub} ${spanne}`,
            kleur: 'kosten',
            // Een prijs per liter delen door het aantal auto's zegt niets.
            perAuto: false,
        };
    })
        .filter((t) => t.reeks.some((p) => !p.leeg));
}
//# sourceMappingURL=tracker.js.map